import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Stepper from '@mui/material/Stepper';
import Typography from '@mui/material/Typography';
import { joiResolver } from '@hookform/resolvers/joi';
import { useSnackbar } from 'notistack';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { usePartnersSignup, usePartnersUpdate } from '@/api/hooks';
import Button from '@/components/Button';
import FormGlobalError from '@/components/forms/FormGlobalError';
import ContactStep from '@/domain/onboarding/components/ContactStep';
import OrganizationStep from '@/domain/onboarding/components/OrganizationStep';
import ReviewStep from '@/domain/onboarding/components/ReviewStep';
import { buildOnboardingSchema } from '@/domain/onboarding/schemas';
import {
  type OnboardingFormValues,
  type OnboardingMode,
  type OnboardingStepKey,
} from '@/domain/onboarding/types';
import {
  getStepForField,
  isOnboardingVisibleField,
  ONBOARDING_STEP_FIELDS,
  ONBOARDING_STEPS,
  toCreatePartnerAccountRequest,
  toPartnerAccountBody,
} from '@/domain/onboarding/utils';
import { useApiFormErrors, useServerErrorsStore } from '@/hooks/useApiFormErrors';
import { useTranslate } from '@/state/i18n';
import { ScreenRoutes } from '@/types/router';
import { normalizeApiError } from '@/utils/error';
import { withServerErrors } from '@/utils/errors';
import { radiusTokens } from '@/theme/themePrimitives';

interface Props {
  mode: OnboardingMode;
  defaultValues: OnboardingFormValues;
}

/** The three-step partner account form, for the first submission and for edit and resubmit. */
const OnboardingForm = ({ mode, defaultValues }: Props) => {
  const t = useTranslate('domain.onboarding.components.OnboardingForm');
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [step, setStep] = useState<OnboardingStepKey>('organization');
  const stepIndex = ONBOARDING_STEPS.indexOf(step);
  const isLastStep = stepIndex === ONBOARDING_STEPS.length - 1;

  const serverErrors = useServerErrorsStore();
  const resolver = useMemo(
    () => withServerErrors(joiResolver(buildOnboardingSchema(mode)), serverErrors),
    [mode, serverErrors],
  );
  const { control, handleSubmit, setError, getValues, setValue, trigger } =
    useForm<OnboardingFormValues>({
      resolver,
      mode: 'onTouched',
      defaultValues,
    });

  const { applyApiError, globalError, clearGlobalError, clearApiErrors } =
    useApiFormErrors<OnboardingFormValues>({
      setError,
      getValues,
      serverErrors,
      isVisibleField: isOnboardingVisibleField,
      fallbackMessage: t('submitError'),
    });

  const { mutateAsync: signup, isPending: isCreating } = usePartnersSignup();
  const { mutateAsync: update, isPending: isUpdating } = usePartnersUpdate();

  const goToNextStep = async () => {
    if (await trigger(ONBOARDING_STEP_FIELDS[step], { shouldFocus: true })) {
      setStep(ONBOARDING_STEPS[stepIndex + 1]);
    }
  };

  const onSubmit = async (values: OnboardingFormValues) => {
    clearApiErrors();
    try {
      if (mode === 'create') await signup(toCreatePartnerAccountRequest(values));
      else await update(toPartnerAccountBody(values));
      enqueueSnackbar(t(mode === 'create' ? 'created' : 'saved'), { variant: 'success' });
      navigate(ScreenRoutes.HOME, { replace: true });
    } catch (error) {
      applyApiError(error);
      const firstField = normalizeApiError(error).fieldErrors?.[0]?.field;
      const errorStep = firstField ? getStepForField(firstField) : null;
      if (errorStep) setStep(errorStep);
    }
  };

  // Client errors on an earlier step (e.g. edited from the review) send the user back to it.
  const onInvalid = (errors: Partial<Record<keyof OnboardingFormValues, unknown>>) => {
    const firstStep = ONBOARDING_STEPS.find((key) =>
      ONBOARDING_STEP_FIELDS[key].some((field) => field in errors),
    );
    if (firstStep) setStep(firstStep);
  };

  return (
    <Box component="form" noValidate onSubmit={handleSubmit(onSubmit, onInvalid)} sx={sx.root}>
      <Stack gap={0.75}>
        <Typography component="h1" variant="h2">
          {t(mode === 'create' ? 'title' : 'editTitle')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t(mode === 'create' ? 'subtitle' : 'editSubtitle')}
        </Typography>
      </Stack>
      <Stepper activeStep={stepIndex} alternativeLabel={false} sx={sx.stepper}>
        {ONBOARDING_STEPS.map((key) => (
          <Step key={key}>
            <StepLabel>{t(`steps.${key}`)}</StepLabel>
          </Step>
        ))}
      </Stepper>
      <FormGlobalError message={globalError} onClose={clearGlobalError} />
      <Paper variant="outlined" sx={sx.paper}>
        {step === 'organization' ? (
          <OrganizationStep control={control} setValue={setValue} />
        ) : null}
        {step === 'contact' ? <ContactStep control={control} /> : null}
        {step === 'review' ? (
          <ReviewStep control={control} mode={mode} onEditStep={setStep} />
        ) : null}
      </Paper>
      <Box sx={sx.footer}>
        {stepIndex === 0 ? (
          <Button variant="text" onClick={() => navigate(ScreenRoutes.HOME)}>
            {t(mode === 'create' ? 'later' : 'cancel')}
          </Button>
        ) : (
          <Button variant="text" onClick={() => setStep(ONBOARDING_STEPS[stepIndex - 1])}>
            {t('back')}
          </Button>
        )}
        {/*
          Distinct keys: without them React reuses the same <button> and turns "Continuar" into
          type="submit" during its own click, so reaching the review step submitted the form.
        */}
        {isLastStep ? (
          <Button key="submit" type="submit" variant="contained" loading={isCreating || isUpdating}>
            {t(mode === 'create' ? 'submit' : 'resubmit')}
          </Button>
        ) : (
          <Button key="next" variant="contained" onClick={goToNextStep}>
            {t('next')}
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default OnboardingForm;

const sx = {
  root: {
    width: '100%',
    maxWidth: 880,
    mx: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: 3,
  },
  stepper: { '& .MuiStepLabel-label': { fontWeight: 500 } },
  paper: { p: { xs: 2.5, sm: 3.5 }, borderRadius: radiusTokens.xxl },
  footer: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 },
};
